'use client';
import React from 'react';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { DynamicCloud } from './DynamicCloud';
import { CardStack } from './CardStack';
import { Button } from '@components/MovingBorders';

export type GenericItem = {
  className?: string;
  title?: string;
  link?: string;
  thumbnail?: string;
  impact?: string[];
  icons?: string[];
  roles?: string[];
};

type ParallaxSectionProps = {
  title: string;
  subtitle: string;
  data: GenericItem[];
  className?: string;
};

export const ParallaxSection = ({ title, subtitle, data, className }: ParallaxSectionProps) => {
  const firstRow = data.slice(0, 2);
  const secondRow = data.slice(2, 4);
  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end start']
  });

  const springConfig = { stiffness: 300, damping: 30, bounce: 100 };
  const rotateX = useSpring(useTransform(scrollYProgress, [0, 0.2], [15, 0]), springConfig);
  const opacity = useSpring(useTransform(scrollYProgress, [0, 0.2], [0.2, 1]), springConfig);
  const rotateZ = useSpring(useTransform(scrollYProgress, [0, 0.2], [20, 0]), springConfig);
  const translateY = useSpring(useTransform(scrollYProgress, [0, 0.2], [-700, 100]), springConfig);
  return (
    <div
      ref={ref}
      className="h-[300vh] py-40 overflow-hidden  antialiased relative flex flex-col self-auto [perspective:1000px] [transform-style:preserve-3d]"
    >
      <Header title={title} subtitle={subtitle} />

      <motion.div
        style={{
          rotateX,
          rotateZ,
          translateY,
          opacity
        }}
        className={className}
      >
        <>
          <motion.div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-20 mb-20 text-center justify-center items-center">
            {firstRow.map((product) => (
              <ItemCard item={product} key={product.title} />
            ))}
          </motion.div>
          <motion.div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-20 mb-20 text-center justify-center items-center">
            {secondRow.map((product) => (
              <ItemCard item={product} key={product.title} />
            ))}
          </motion.div>
          <div
            className="absolute pointer-events-none inset-0 flex items-center justify-center dark:bg-black-100
         bg-white [mask-image:radial-gradient(ellipse_at_center,transparent_20%,black)]"
          />
        </>
      </motion.div>
    </div>
  );
};

export const Header = ({ title, subtitle }: { title: string; subtitle: string }) => {
  return (
    <div className="max-w-7xl relative mx-auto py-20 md:py-40 px-4 w-full  left-0 top-0">
      <h1 className="text-2xl md:text-7xl font-bold dark:text-white">{title}</h1>
      <p className="max-w-2xl text-base md:text-xl mt-8 dark:text-neutral-200">{subtitle}</p>
    </div>
  );
};

export const ItemCard = ({ item }: { item: GenericItem }) => {
  return (
    <motion.div
      whileHover={{
        y: -20
      }}
      key={item?.title}
      className="sm:w-[60rem] sm:w-[60rem] 
      relative rounded-xl  z-10"
    >
      <h2 className="text-3xl font-bold mb-4 left-4 ">{item?.title}</h2>
      <Button duration={Math.floor(Math.random() * 10000) + 10000}>
        <>
          {item?.icons ? <DynamicCloud iconSlugs={item.icons} /> : null}
          {item?.impact ? (
            <div className="">
              <CardStack
                items={item.impact.map((impact, index) => ({
                  id: index,
                  name: impact,
                  designation: '',
                  content: impact
                }))}
              />
            </div>
          ) : null}
          {item?.roles ? (
            <div className=" ">
              {item.roles.map((role, index) => (
                <div key={index} className="p-2">
                  <div className="dark:bg-black bg-white rounded-3xl p-4 shadow-xl border border-neutral-200 dark:border-white/[0.1] shadow-black/[0.1] dark:shadow-white/[0.05]">
                    {role}
                  </div>
                </div>
              ))}
            </div>
          ) : null}
          {!item?.icons && !item?.impact && !item?.roles ? (
            <button onClick={() => window.open(item?.link as string, '_blank')} className="w-full h-full border-0 p-0">
              <img
                src={item?.thumbnail}
                className="object-cover h-full w-full inset-0"
                alt={item?.title || 'Project thumbnail'}
              />
            </button>
          ) : null}
        </>
      </Button>
    </motion.div>
  );
};
